// Two visually-distinct empty states for the Alerts list (S11-6 AC):
//
// 1. `noRules` — no alert rules configured. Tone: informational/CTA.
//    Icon: bell outline. Copy + PrimaryButton linking to /alerts/rules.
//
// 2. `noActive` — active filter matched zero rows (the happy path —
//    rules exist, nothing is firing). Tone: positive/reassuring.
//    Icon: green check. If a resolved alert exists in the last 24h,
//    show "Last alert resolved <relative>" below the headline.
//
// Kept as separate components so the callsite's decision logic stays
// readable and the two variants don't leak shared layout concerns.

import { Link } from 'react-router-dom';
import PrimaryButton from '../shared/PrimaryButton.jsx';

function relativeTime(iso) {
  if (!iso) return null;
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms) || ms < 0) return null;
  const mins = Math.floor(ms / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} minute${mins === 1 ? '' : 's'} ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
}

function isWithin24h(iso) {
  if (!iso) return false;
  const ms = Date.now() - new Date(iso).getTime();
  return ms >= 0 && ms <= 24 * 60 * 60 * 1000;
}

export function NoActiveAlertsEmptyState({ lastResolvedAt }) {
  const recent = isWithin24h(lastResolvedAt) ? relativeTime(lastResolvedAt) : null;
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <svg
        aria-hidden="true"
        className="mb-3 h-10 w-10 text-green-500"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        viewBox="0 0 24 24"
      >
        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
      </svg>
      <p className="text-sm font-medium text-gray-900">No active alerts.</p>
      {recent && (
        <p className="mt-1 text-xs text-gray-500">Last alert resolved {recent}.</p>
      )}
    </div>
  );
}

export function NoRulesEmptyState() {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center bg-gray-50 rounded-lg border border-dashed border-gray-300">
      <svg
        aria-hidden="true"
        className="mb-3 h-10 w-10 text-gray-400"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
        />
      </svg>
      <p className="text-sm font-medium text-gray-900">
        No alert rules have been configured.
      </p>
      <p className="mt-1 text-xs text-gray-500 max-w-sm">
        Set up alert rules to begin monitoring. Rules define the conditions
        that trigger alerts from your agents.
      </p>
      <div className="mt-4">
        <Link to="/alerts/rules">
          <PrimaryButton>Configure Alert Rules</PrimaryButton>
        </Link>
      </div>
    </div>
  );
}
